import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import ListPager from '../components/ListPager';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 50;

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [filterName, setFilterName] = useState('');
  const [filterClinic, setFilterClinic] = useState('');
  const [searchParams] = useSearchParams();
  const [paidFilter, setPaidFilter] = useState(
    searchParams.get('paid') === 'gt0' ? 'paid' : 'all'
  );
  const navigate = useNavigate();

  const key = localStorage.getItem('adminKey');

  const fetchData = useCallback(() => {
    const params = {
      search: filterName || undefined,
      target_search: filterClinic || undefined,
      paid: paidFilter === 'paid' ? 'paid' : undefined,
      sort: sortField, order: sortDir,
      limit: LIMIT, offset,
    };
    axios.get(`${API}/admin/patients`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setPatients(r.data.items); setTotal(r.data.total); });
  }, [filterName, filterClinic, paidFilter, sortField, sortDir, offset, key]);

  useEffect(() => {
    const t = setTimeout(() => fetchData(), 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  const toggleSort = (field) => {
    setOffset(0);
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const arrow = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const onFilter = (setter) => (e) => { setter(e.target.value); setOffset(0); };

  return (
    <Layout>
      <h1 style={styles.title}>Пациенты ({total})</h1>

      <div style={styles.table}>
        <div style={styles.thead}>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('name')}>
            Пациент{arrow('name')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('birth_date')}>
            Дата рождения{arrow('birth_date')}
          </div>
          <div style={styles.th}>Клиники</div>
          <div style={styles.th}>Направившие врачи</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('referrals_count')}>
            Направлений{arrow('referrals_count')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('total_paid')}>
            Всего оплат{arrow('total_paid')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('created_at')}>
            Добавлен{arrow('created_at')}
          </div>
        </div>

        <div style={styles.filterRow}>
          <div style={styles.filterCell}>
            <input
              style={styles.filterInput}
              placeholder="Поиск по ФИО"
              value={filterName}
              onChange={onFilter(setFilterName)}
            />
          </div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}>
            <input
              style={styles.filterInput}
              placeholder="клиника / врач"
              value={filterClinic}
              onChange={onFilter(setFilterClinic)}
            />
          </div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}>
            <select
              style={styles.filterInput}
              value={paidFilter}
              onChange={onFilter(setPaidFilter)}
            >
              <option value="all">Любые</option>
              <option value="paid">Оплатившие (&gt;0)</option>
            </select>
          </div>
          <div style={styles.filterCell}></div>
        </div>

        {patients.map(p => (
          <div key={p.id} style={styles.row}>
            <div style={styles.td}>
              <div style={styles.nameLink} onClick={() => navigate(`/patients/${p.id}`)}>{p.name}</div>
            </div>
            <div style={styles.td}>{p.birth_date ? new Date(p.birth_date).toLocaleDateString('ru') : '—'}</div>
            <div style={styles.td}>
              {p.clinics.length ? p.clinics.join(', ') : <span style={styles.muted}>—</span>}
            </div>
            <div style={styles.td}>
              {p.doctors.length ? p.doctors.join(', ') : <span style={styles.muted}>—</span>}
            </div>
            <div style={styles.td}>{p.referrals_count}</div>
            <div style={styles.td}>
              {(p.total_paid || 0) > 0
                ? <span style={{ color: '#2EC4B6', fontWeight: 600 }}>{fmtMoney(p.total_paid)} ₽</span>
                : <span style={styles.muted}>{fmtMoney(0)} ₽</span>}
            </div>
            <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>
              {new Date(p.created_at).toLocaleDateString('ru')}
            </div>
          </div>
        ))}

        {patients.length === 0 && (
          <div style={styles.empty}>Ничего не найдено</div>
        )}
      </div>

      <ListPager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
    </Layout>
  );
}

const cols = '1.8fr 1.1fr 1.6fr 1.6fr 0.9fr 1fr 1fr';

const styles = {
  title: { color: 'white', fontSize: 24, fontWeight: 700, marginBottom: 24, marginTop: 0 },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'grid', gridTemplateColumns: cols, padding: '12px 20px', borderBottom: '1px solid #334155' },
  th: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  filterRow: { display: 'grid', gridTemplateColumns: cols, padding: '8px 20px', borderBottom: '1px solid #334155', background: '#172033', gap: 8 },
  filterCell: { display: 'flex', alignItems: 'center' },
  filterInput: {
    width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155',
    borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box',
  },
  row: { display: 'grid', gridTemplateColumns: cols, padding: '16px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0' },
  td: { fontSize: 14, color: '#e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  name: { fontWeight: 600, marginBottom: 2 },
  nameLink: { fontWeight: 600, marginBottom: 2, color: '#2EC4B6', cursor: 'pointer' },
  muted: { color: '#64748b' },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '40px 0' },
};
